import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createJob,
  patchJob,
  getJob,
  deleteJob,
  listMyJobs,
  getSyndication,
  getSyndicationLinks,
  generateJD,
  getCandidateProfile,
  updateApplicationNote,
  bulkApplicationAction,
  getScreeningQuestions,
  distributeJob,
  getDistributionStatus,
  getBilling,
} from "@/lib/companyApi";
import { createThread } from "@/lib/messagesApi";
import { scheduleInvite } from "@/lib/schedulingApi";
import ScreeningBreakdown from "@/components/wishire/ScreeningBreakdown";
import {
  Plus,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Trash2,
  Copy,
  Users,
  Sparkles,
  X,
  Tag,
  MessageSquare,
  CheckSquare,
  Square,
  ChevronDown,
  Star,
  AlertCircle,
  Briefcase,
  Send,
  CalendarClock,
} from "lucide-react";
import { useNavigate as useNav } from "react-router-dom";

const EMPLOYMENT = ["full_time", "part_time", "contract", "internship"];

// ----------------------------------------------------------------
// Jobs List
// ----------------------------------------------------------------
export function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await listMyJobs();
      setJobs(data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">All jobs</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Your open &amp; closed roles.</h1>
        </div>
        <Link to="/company/jobs/new" data-testid="jobs-new" className="btn-primary">
          <Plus size={16} /> Post a job
        </Link>
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : jobs.length === 0 ? (
        <p className="text-stone-500">No jobs yet. Click <strong>Post a job</strong> above.</p>
      ) : (
        <ul className="divide-y divide-stone-200 border border-stone-200 rounded-3xl overflow-hidden bg-white">
          {jobs.map((j) => (
            <li key={j.job_id} className="p-5 md:p-7 flex flex-col md:flex-row md:items-center gap-4 hover:bg-stone-50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest border ${
                    j.status === "open" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : j.status === "draft" ? "bg-stone-100 border-stone-300 text-stone-700"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}>{j.status}</span>
                  <span className="overline">{j.target_role}</span>
                </div>
                <h3 className="font-display text-xl">{j.title}</h3>
                <p className="text-stone-500 text-sm">
                  {j.location || (j.remote ? "Remote" : "—")} · {j.employment_type?.replace("_", " ")}
                </p>
              </div>
              <div className="text-stone-700 inline-flex items-center gap-2 text-sm">
                <Users size={14} /> {j.applications_count || 0}
              </div>
              <Link to={`/company/jobs/${j.job_id}`} data-testid={`jobs-open-${j.job_id}`} className="btn-ghost text-sm py-2 px-4">
                Open
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// New Job — with AI JD Generator
// ----------------------------------------------------------------
export function JobNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", target_role: "", description: "", requirements: "", skills: "",
    location: "", remote: false, employment_type: "full_time",
    comp_min: "", comp_max: "", currency: "INR", status: "open",
    screening_enabled: false, screening_min_score: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    getBilling().then(({ data }) => setBilling(data)).catch(() => {});
  }, []);

  // AI JD Generator state
  const [jdRole, setJdRole] = useState("");
  const [jdSkills, setJdSkills] = useState("");
  const [jdExp, setJdExp] = useState("2-4 years");
  const [jdLoading, setJdLoading] = useState(false);
  const [jdOpen, setJdOpen] = useState(false);

  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleGenerateJD = async () => {
    if (!jdRole.trim()) return;
    setJdLoading(true);
    try {
      const skillsArr = jdSkills.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await generateJD(jdRole, skillsArr, jdExp);
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        target_role: jdRole,
        description: data.description || f.description,
        requirements: (data.requirements || []).join("\n"),
        skills: (data.skills || skillsArr).join(", "),
        employment_type: data.employment_type || f.employment_type,
      }));
      setJdOpen(false);
    } catch (e) {
      toast.error("JD generation failed. Try again.");
    } finally {
      setJdLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.target_role.trim() || !form.description.trim()) {
      setError("Title, target role and description are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        comp_min: form.comp_min ? Number(form.comp_min) : null,
        comp_max: form.comp_max ? Number(form.comp_max) : null,
        screening_min_score: form.screening_min_score ? Number(form.screening_min_score) : null,
      };
      const { data } = await createJob(payload);
      navigate(`/company/jobs/${data.job_id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create job");
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-10 md:py-14 max-w-4xl">
      <Link to="/company/jobs" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft size={14} /> Back to jobs
      </Link>
      <div className="flex items-end justify-between mb-8">
        <h1 className="font-display text-4xl md:text-5xl leading-tight">
          Post a job <span className="italic text-stone-500">in 60 seconds.</span>
        </h1>
        <button
          type="button"
          onClick={() => setJdOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors text-sm font-medium"
          data-testid="jd-generate-btn"
        >
          <Sparkles size={15} /> AI JD Generator
        </button>
      </div>

      {/* AI JD Generator Panel */}
      {jdOpen && (
        <div className="mb-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg flex items-center gap-2">
              <Sparkles size={16} className="text-amber-600" /> Generate job description with AI
            </h3>
            <button onClick={() => setJdOpen(false)} className="text-stone-500 hover:text-stone-900">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-1">
              <label className="overline">Role / Title *</label>
              <input
                value={jdRole}
                onChange={(e) => setJdRole(e.target.value)}
                placeholder="Senior Backend Engineer"
                className="mt-2 w-full px-4 py-2.5 rounded-full bg-white border border-amber-200 focus:outline-none focus:border-amber-500 text-sm"
              />
            </div>
            <div>
              <label className="overline">Key skills (comma-separated)</label>
              <input
                value={jdSkills}
                onChange={(e) => setJdSkills(e.target.value)}
                placeholder="Go, Kafka, AWS"
                className="mt-2 w-full px-4 py-2.5 rounded-full bg-white border border-amber-200 focus:outline-none focus:border-amber-500 text-sm"
              />
            </div>
            <div>
              <label className="overline">Experience</label>
              <input
                value={jdExp}
                onChange={(e) => setJdExp(e.target.value)}
                placeholder="2-4 years"
                className="mt-2 w-full px-4 py-2.5 rounded-full bg-white border border-amber-200 focus:outline-none focus:border-amber-500 text-sm"
              />
            </div>
          </div>
          <button
            onClick={handleGenerateJD}
            disabled={jdLoading || !jdRole.trim()}
            className="btn-primary disabled:opacity-60"
          >
            {jdLoading ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate &amp; fill form</>}
          </button>
          <p className="text-xs text-amber-700 mt-3">AI will auto-fill the form below. Review before posting.</p>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <F label="Job title *" testId="job-title" value={form.title} onChange={change("title")} placeholder="Senior Backend Engineer" full />
        <F label="Target role (used for AI matching) *" testId="job-target-role" value={form.target_role} onChange={change("target_role")} placeholder="Senior Backend Engineer" full />
        <div className="md:col-span-2">
          <label className="overline">Description *</label>
          <textarea
            data-testid="job-description"
            value={form.description}
            onChange={change("description")}
            rows={6}
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
            placeholder="What the role is about, the impact, what success looks like in 6 months…"
          />
        </div>
        <div>
          <label className="overline">Must-haves (one per line)</label>
          <textarea
            data-testid="job-requirements"
            value={form.requirements}
            onChange={change("requirements")}
            rows={4}
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
            placeholder="5+ years backend&#10;Strong distributed systems&#10;Go or Java"
          />
        </div>
        <div>
          <label className="overline">Skills (comma-separated)</label>
          <textarea
            data-testid="job-skills"
            value={form.skills}
            onChange={change("skills")}
            rows={4}
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
            placeholder="Go, PostgreSQL, Kafka, Kubernetes, AWS"
          />
        </div>
        <F label="Location" testId="job-location" value={form.location} onChange={change("location")} placeholder="Bengaluru, India" />
        <div>
          <label className="overline">Employment type</label>
          <select
            data-testid="job-employment-type"
            value={form.employment_type}
            onChange={change("employment_type")}
            className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
          >
            {EMPLOYMENT.map((e) => (
              <option key={e} value={e}>{e.replace("_", " ")}</option>
            ))}
          </select>
        </div>
        <F label="Comp min (₹/year)" testId="job-comp-min" value={form.comp_min} onChange={change("comp_min")} placeholder="2500000" />
        <F label="Comp max (₹/year)" testId="job-comp-max" value={form.comp_max} onChange={change("comp_max")} placeholder="4500000" />
        <label className="md:col-span-2 inline-flex items-center gap-3 text-stone-700 mt-2">
          <input type="checkbox" data-testid="job-remote" checked={form.remote} onChange={change("remote")} className="w-5 h-5" />
          Remote-friendly
        </label>

        {/* Pre-apply screening interview (premium) */}
        <div className="md:col-span-2 mt-2 rounded-2xl border border-stone-200 bg-[#F5F3ED] p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="job-screening"
              checked={form.screening_enabled}
              onChange={change("screening_enabled")}
              disabled={billing && !billing.features?.screening}
              className="w-5 h-5 mt-0.5 accent-[#C2410C]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-stone-900">Require a pre-apply screening interview</span>
                <span className="text-[10px] font-mono uppercase tracking-widest bg-[#C2410C] text-white px-2 py-0.5 rounded-full">1 credit</span>
              </div>
              <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                Candidates must complete a short AI mock interview personalized to this JD before they
                can apply. Their score attaches to the application — you only review serious, prepared applicants.
              </p>
              {billing && !billing.features?.screening ? (
                <p className="mt-2 text-xs text-amber-700">
                  Screening is a Growth/Scale feature. <Link to="/company/billing" className="underline">Upgrade your plan →</Link>
                </p>
              ) : billing ? (
                <p className="mt-2 text-xs text-stone-500">You have <strong>{billing.credits}</strong> credit(s). <Link to="/company/billing" className="underline">Manage →</Link></p>
              ) : null}

              {form.screening_enabled && (
                <div className="mt-3">
                  <label className="overline">Minimum score to apply (optional)</label>
                  <input
                    type="number" min="0" max="100"
                    data-testid="job-screening-min"
                    value={form.screening_min_score}
                    onChange={change("screening_min_score")}
                    placeholder="e.g. 60 — leave blank to allow all"
                    className="mt-1 w-full md:w-64 px-4 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900"
                  />
                </div>
              )}
            </div>
          </label>
        </div>
      </form>

      {error && <p className="text-red-700 text-sm mt-4" data-testid="job-error">{error}</p>}

      <div className="mt-8 flex items-center gap-3">
        <button type="button" onClick={submit} disabled={submitting} data-testid="job-submit" className="btn-primary disabled:opacity-60">
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Posting</> : "Publish job"}
        </button>
        <button type="button" onClick={() => navigate(-1)} className="text-sm text-stone-600 hover:text-stone-900">
          Cancel
        </button>
      </div>
    </div>
  );
}

function F({ label, testId, value, onChange, placeholder, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="overline">{label}</label>
      <input
        type="text"
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
      />
    </div>
  );
}

// ----------------------------------------------------------------
// Job Detail
// ----------------------------------------------------------------
export function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [posts, setPosts] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [syndicationLinks, setSyndicationLinks] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [distributing, setDistributing] = useState(false);
  const [distResult, setDistResult] = useState(null);
  const [distStatus, setDistStatus] = useState(null);

  const load = async () => {
    const { data } = await getJob(id);
    setJob(data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const [postsRes, linksRes] = await Promise.all([
        getSyndication(id),
        getSyndicationLinks(id).catch(() => ({ data: null })),
      ]);
      setPosts(postsRes.data);
      setSyndicationLinks(linksRes.data);
    } catch (e) {}
    finally { setLoadingPosts(false); }
  };

  const loadDistStatus = async () => {
    try {
      const { data } = await getDistributionStatus(id);
      setDistStatus(data);
    } catch (e) {}
  };

  const handleDistribute = async () => {
    setDistributing(true);
    setDistResult(null);
    try {
      const { data } = await distributeJob(id);
      setDistResult(data);
      await loadDistStatus();
    } catch (e) {
      setDistResult({ error: e?.response?.data?.detail || "Distribution failed" });
    } finally {
      setDistributing(false);
    }
  };

  if (!job) return <div className="container-x py-10 text-stone-500">Loading…</div>;

  const toggleStatus = async () => {
    const next = job.status === "open" ? "closed" : "open";
    const { data } = await patchJob(id, { status: next });
    setJob(data);
  };

  const remove = async () => {
    if (!(await confirm({ title: "Please confirm", description: "Delete this job and all its applications?", danger: true }))) return;
    await deleteJob(id);
    navigate("/company/jobs");
  };

  return (
    <div className="container-x py-10 md:py-14">
      <Link to="/company/jobs" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft size={14} /> Back to jobs
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border ${
              job.status === "open" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-stone-100 border-stone-300 text-stone-700"
            }`}>{job.status}</span>
            <span className="overline">{job.target_role}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight" data-testid="job-detail-title">{job.title}</h1>
          <p className="mt-2 text-stone-500">
            {job.location || (job.remote ? "Remote" : "—")} · {job.employment_type?.replace("_", " ")}
            {job.comp_min && job.comp_max ? ` · ₹${(job.comp_min / 100000).toFixed(1)}L – ₹${(job.comp_max / 100000).toFixed(1)}L` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={toggleStatus} data-testid="job-toggle-status" className="btn-ghost text-sm">
            {job.status === "open" ? "Close job" : "Reopen job"}
          </button>
          <button
            onClick={remove}
            data-testid="job-delete"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-300 text-red-700 hover:bg-red-50 transition-colors text-sm"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      <div className="inline-flex p-1 mb-8 rounded-full border border-stone-300 bg-white">
        {[["overview", "Overview"], ["applicants", "Applicants"], ["distribute", "🚀 Distribute"], ["posts", "AI Posts"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`job-tab-${k}`}
            onClick={() => {
              setActiveTab(k);
              if (k === "posts" && !posts) fetchPosts();
              if (k === "distribute" && !distStatus) loadDistStatus();
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              activeTab === k ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {activeTab === "distribute" && (
        <DistributeView
          job={job}
          distributing={distributing}
          distResult={distResult}
          distStatus={distStatus}
          onDistribute={handleDistribute}
        />
      )}

      {activeTab === "overview" && (
        <div className="rounded-3xl bg-white border border-stone-200 p-7">
          <p className="overline">Description</p>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-stone-800 leading-relaxed">{job.description}</pre>
          {job.requirements?.length > 0 && (
            <>
              <p className="overline mt-7">Must-haves</p>
              <ul className="mt-3 space-y-1.5 text-stone-800">
                {job.requirements.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            </>
          )}
          {job.skills?.length > 0 && (
            <>
              <p className="overline mt-7">Skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {job.skills.map((s, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-sm">{s}</span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "applicants" && <Applicants jobId={id} />}
      {activeTab === "posts" && <SyndicationView loading={loadingPosts} posts={posts} onRefresh={fetchPosts} syndicationLinks={syndicationLinks} jobId={id} />}

    </div>
  );
}

// ----------------------------------------------------------------
// Applicants — with bulk actions, candidate modal, notes/tags
// ----------------------------------------------------------------
const STAGES = [
  { k: "applied", l: "Applied" },
  { k: "screening", l: "Screening" },
  { k: "interview", l: "Interview" },
  { k: "offer", l: "Offer" },
  { k: "hired", l: "Hired" },
  { k: "rejected", l: "Rejected" },
];

function Applicants({ jobId }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkStage, setBulkStage] = useState("screening");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [profileModal, setProfileModal] = useState(null); // user_id

  const load = async () => {
    const { listJobApplications } = await import("@/lib/companyApi");
    const { data } = await listJobApplications(jobId);
    setApps(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (applicationId, status) => {
    const { updateApplication } = await import("@/lib/companyApi");
    await updateApplication(applicationId, { status });
    setApps((arr) => arr.map((a) => (a.application_id === applicationId ? { ...a, status } : a)));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === apps.length) setSelected(new Set());
    else setSelected(new Set(apps.map((a) => a.application_id)));
  };

  const handleBulkMove = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await bulkApplicationAction([...selected], "move_stage", bulkStage);
      setApps((arr) => arr.map((a) => selected.has(a.application_id) ? { ...a, status: bulkStage } : a));
      setSelected(new Set());
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selected.size === 0 || !(await confirm({ title: "Please confirm", description: `Reject ${selected.size} candidates?`, danger: true }))) return;
    setBulkLoading(true);
    try {
      await bulkApplicationAction([...selected], "reject");
      setApps((arr) => arr.map((a) => selected.has(a.application_id) ? { ...a, status: "rejected" } : a));
      setSelected(new Set());
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSaveNote = async (applicationId, note, tags) => {
    await updateApplicationNote(applicationId, note, tags);
    setApps((arr) => arr.map((a) => a.application_id === applicationId ? { ...a, recruiter_note: note, tags } : a));
  };

  if (loading) return <p className="text-stone-500">Loading applicants…</p>;
  if (apps.length === 0)
    return (
      <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-600">
        No applicants yet. Share your job link or generate platform posts.
      </div>
    );

  return (
    <>
      {/* Bulk action bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5 p-3 rounded-2xl bg-white border border-stone-200">
        <button
          onClick={selectAll}
          className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900"
        >
          {selected.size === apps.length ? <CheckSquare size={16} /> : <Square size={16} />}
          {selected.size > 0 ? `${selected.size} selected` : "Select all"}
        </button>

        {selected.size > 0 && (
          <>
            <div className="h-4 w-px bg-stone-300" />
            <span className="text-sm text-stone-600">Move to:</span>
            <select
              value={bulkStage}
              onChange={(e) => setBulkStage(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-full border border-stone-300 bg-white focus:outline-none"
            >
              {STAGES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
            </select>
            <button
              onClick={handleBulkMove}
              disabled={bulkLoading}
              className="text-sm px-4 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 transition-colors disabled:opacity-60"
            >
              {bulkLoading ? <Loader2 size={13} className="animate-spin" /> : "Move"}
            </button>
            <button
              onClick={handleBulkReject}
              disabled={bulkLoading}
              className="text-sm px-4 py-1.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-60"
            >
              Reject all
            </button>
          </>
        )}

        <span className="ml-auto text-xs text-stone-400 font-mono">{apps.length} total</span>
      </div>

      {/* Kanban board */}
      <div className="overflow-x-auto -mx-4 px-4">
        <div className="flex gap-4 min-w-max pb-4">
          {STAGES.map((s) => {
            const inStage = apps.filter((a) => a.status === s.k);
            return (
              <div
                key={s.k}
                data-testid={`stage-${s.k}`}
                className="w-72 shrink-0 rounded-2xl bg-[#F5F3ED] border border-stone-200 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="overline">{s.l}</p>
                  <span className="text-xs font-mono text-stone-500">{inStage.length}</span>
                </div>
                <div className="space-y-3">
                  {inStage.map((a) => (
                    <ApplicantCard
                      key={a.application_id}
                      app={a}
                      selected={selected.has(a.application_id)}
                      onToggle={() => toggleSelect(a.application_id)}
                      onMove={(st) => move(a.application_id, st)}
                      onOpenProfile={() => setProfileModal({ userId: a.user_id, jobId, applicationId: a.application_id })}
                      onSaveNote={(note, tags) => handleSaveNote(a.application_id, note, tags)}
                    />
                  ))}
                  {inStage.length === 0 && <p className="text-xs text-stone-400">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Candidate Profile Modal */}
      {profileModal && (
        <CandidateProfileModal
          userId={profileModal.userId}
          jobId={profileModal.jobId}
          applicationId={profileModal.applicationId}
          onClose={() => setProfileModal(null)}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------
// Applicant Card — with checkbox, note, tags, profile button
// ----------------------------------------------------------------
function ApplicantCard({ app, selected, onToggle, onMove, onOpenProfile, onSaveNote }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(app.recruiter_note || "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(app.tags || []);
  const [saving, setSaving] = useState(false);

  const saveNote = async () => {
    setSaving(true);
    try {
      await onSaveNote(note, tags);
      setNoteOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const addTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      const t = tagInput.trim();
      if (!tags.includes(t)) setTags((prev) => [...prev, t]);
      setTagInput("");
    }
  };

  return (
    <div
      className={`rounded-xl bg-white border transition-all ${selected ? "border-[#C2410C] shadow-sm" : "border-stone-200"} p-3`}
      data-testid={`applicant-${app.application_id}`}
    >
      <div className="flex items-center gap-2">
        <button onClick={onToggle} className="text-stone-400 hover:text-[#C2410C] shrink-0">
          {selected ? <CheckSquare size={14} className="text-[#C2410C]" /> : <Square size={14} />}
        </button>
        {app.candidate_picture ? (
          <img src={app.candidate_picture} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center text-xs font-medium shrink-0">
            {(app.candidate_name || "?")[0]}
          </div>
        )}
        <p className="text-sm font-medium truncate flex-1" title={app.candidate_name}>{app.candidate_name}</p>
      </div>

      {/* Submitted by an agency */}
      {app.agency_name && (
        <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full" title={app.agency_note || undefined}>
          via {app.agency_name}
        </span>
      )}

      {/* Score pill */}
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Wishire score</span>
        <ScorePill score={app.wishire_score} />
      </div>

      {/* Pre-apply screening score + dimension breakdown + integrity */}
      {app.screening_score != null && (
        <div className="mt-2 rounded-lg bg-[#fff7ed] border border-[#C2410C]/20 p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#C2410C]">Screening</span>
            <span className="font-mono text-sm font-bold text-stone-900">{app.screening_score}/100</span>
          </div>
          {app.screening_breakdown && <ScreeningBreakdown breakdown={app.screening_breakdown} compact />}
          {app.screening_integrity && app.screening_integrity.risk !== "low" && (
            <div className={`mt-2 rounded-md px-2 py-1.5 text-[11px] leading-snug ${
              app.screening_integrity.risk === "high" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"
            }`} title={(app.screening_integrity.flags || []).join(" · ")}>
              <span className="inline-flex items-center gap-1 font-semibold">
                <AlertTriangle size={11} /> Integrity: {app.screening_integrity.risk} risk ({app.screening_integrity.score}/100)
              </span>
              {(app.screening_integrity.flags || []).length > 0 && (
                <span className="block text-[10px] mt-0.5 opacity-90">{app.screening_integrity.flags[0]}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {tags.map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px]">{t}</span>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        <button
          onClick={onOpenProfile}
          className="text-[11px] px-2 py-0.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-100 transition-colors"
        >
          View profile
        </button>
        <button
          onClick={() => setNoteOpen((v) => !v)}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
            app.recruiter_note ? "border-amber-200 text-amber-700 bg-amber-50" : "border-stone-300 text-stone-600 hover:bg-stone-100"
          }`}
        >
          <MessageSquare size={10} className="inline mr-0.5" />
          {app.recruiter_note ? "Note ✓" : "Note"}
        </button>
        <div className="relative">
          <select
            onChange={(e) => { if (e.target.value) onMove(e.target.value); e.target.value = ""; }}
            defaultValue=""
            className="text-[11px] px-2 py-0.5 rounded-full border border-stone-300 text-stone-600 bg-white appearance-none cursor-pointer"
          >
            <option value="" disabled>→ Move</option>
            {STAGES.filter((s) => s.k !== app.status).map((s) => (
              <option key={s.k} value={s.k}>{s.l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Note + tag panel */}
      {noteOpen && (
        <div className="mt-3 border-t border-stone-100 pt-3">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Internal note (only visible to your team)"
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs focus:outline-none focus:border-stone-400 resize-none"
          />
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={addTag}
            placeholder="Add tag + Enter  (e.g. shortlist, strong-backend)"
            className="mt-2 w-full px-3 py-1.5 rounded-full border border-stone-200 text-xs focus:outline-none focus:border-stone-400"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[10px] cursor-pointer hover:bg-red-50 hover:text-red-600"
                onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
              >
                {t} ×
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={saveNote}
              disabled={saving}
              className="text-xs px-3 py-1 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setNoteOpen(false)} className="text-xs text-stone-500 hover:text-stone-900">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Score Pill
// ----------------------------------------------------------------
function ScorePill({ score }) {
  const color = score >= 80 ? "#15803D" : score >= 60 ? "#C2410C" : "#78716C";
  return (
    <span className="font-display text-lg font-bold" style={{ color }}>{score || "—"}</span>
  );
}

// ----------------------------------------------------------------
// Candidate Profile Modal — deep view + AI fit
// ----------------------------------------------------------------
function CandidateProfileModal({ userId, jobId, applicationId, onClose }) {
  const navigate = useNav();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [messaging, setMessaging] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState(null);

  const handleMessage = async () => {
    setMessaging(true);
    try {
      await createThread(userId, jobId, null);
      navigate("/company/messages");
    } catch (e) {
      setMessaging(false);
    }
  };

  const handleSchedule = async () => {
    setScheduling(true);
    setScheduleMsg(null);
    try {
      await scheduleInvite({
        candidate_id: userId,
        job_id: jobId,
        application_id: applicationId,
      });
      setScheduleMsg({ type: "ok", text: "Booking invite sent to the candidate's email." });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send invite.";
      if (detail.toLowerCase().includes("cal.com")) {
        setScheduleMsg({ type: "err", text: "Connect Cal.com first — go to Schedule in the sidebar.", link: "/company/schedule" });
      } else {
        setScheduleMsg({ type: "err", text: detail });
      }
    } finally {
      setScheduling(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getCandidateProfile(userId, jobId);
        setProfile(data);
      } catch (e) {}
      finally { setLoading(false); }
    })();
  }, [userId, jobId]);

  const c = profile?.candidate || {};
  const fit = profile?.fit;
  const resume = profile?.resume;
  const interview = profile?.interview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7] rounded-3xl border border-stone-200 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#FDFBF7] border-b border-stone-200 px-7 py-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            {c.picture ? (
              <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center font-medium">
                {(c.name || "?")[0]}
              </div>
            )}
            <div>
              <h2 className="font-display text-xl">{c.name || "Candidate"}</h2>
              <p className="text-stone-500 text-sm">{c.headline || c.target_role || c.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSchedule}
              disabled={scheduling}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 disabled:opacity-60 transition-colors text-xs font-medium"
            >
              {scheduling ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
              Schedule
            </button>
            <button
              onClick={handleMessage}
              disabled={messaging}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 disabled:opacity-60 transition-colors text-xs font-medium"
            >
              {messaging ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Message
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Schedule feedback banner */}
        {scheduleMsg && (
          <div className={`px-7 py-2.5 text-sm flex items-center gap-2 ${
            scheduleMsg.type === "ok" ? "bg-emerald-50 text-emerald-700 border-b border-emerald-100" : "bg-red-50 text-red-700 border-b border-red-100"
          }`}>
            {scheduleMsg.type === "ok" ? <CheckSquare size={14} /> : <AlertCircle size={14} />}
            <span>{scheduleMsg.text}</span>
            {scheduleMsg.link && (
              <button onClick={() => navigate(scheduleMsg.link)} className="underline font-medium ml-1">
                Open Schedule
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="p-10 text-center text-stone-500">
            <Loader2 className="animate-spin mx-auto mb-2" size={20} />
            Loading candidate profile…
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="px-7 pt-5">
              <div className="inline-flex p-1 rounded-full border border-stone-300 bg-white mb-5">
                {[["overview", "Overview"], ["fit", "AI Fit"], ["resume", "Resume"], ["interview", "Interview"]].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                      tab === k ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-7 pb-7">
              {/* Overview */}
              {tab === "overview" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoBox label="Email" value={c.email} />
                    <InfoBox label="City" value={c.city || "—"} />
                    <InfoBox label="Experience" value={c.years_experience ? `${c.years_experience} yrs` : "—"} />
                    <InfoBox label="Target Role" value={c.target_role || "—"} />
                  </div>

                  {/* Scores */}
                  <div className="rounded-2xl bg-white border border-stone-200 p-5">
                    <p className="overline mb-3">Wishire Scores</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(c.latest_resume_score) }}>
                          {c.latest_resume_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Resume</p>
                      </div>
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(c.latest_interview_score) }}>
                          {c.latest_interview_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Interview</p>
                      </div>
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(profile?.application?.wishire_score) }}>
                          {profile?.application?.wishire_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Composite</p>
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {c.skills?.length > 0 && (
                    <div>
                      <p className="overline mb-2">Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {c.skills.map((s, i) => (
                          <span key={i} className="px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-sm">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Fit */}
              {tab === "fit" && (
                <div className="space-y-4">
                  {!fit ? (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 text-center text-amber-700">
                      <AlertCircle size={20} className="mx-auto mb-2" />
                      AI fit assessment not available — select a job to enable this.
                    </div>
                  ) : (
                    <>
                      {/* Match % */}
                      <div className="rounded-2xl bg-white border border-stone-200 p-5 flex items-center gap-5">
                        <div className="text-center shrink-0">
                          <p className="font-display text-5xl" style={{ color: scoreColor(fit.match_percent) }}>
                            {fit.match_percent}%
                          </p>
                          <p className="text-xs text-stone-500 mt-1">Match</p>
                        </div>
                        <div>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                            fit.recommendation === "Strong Hire" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                              : fit.recommendation === "Hire" ? "bg-blue-50 text-blue-800 border border-blue-200"
                              : fit.recommendation === "Maybe" ? "bg-amber-50 text-amber-800 border border-amber-200"
                              : "bg-red-50 text-red-800 border border-red-200"
                          }`}>{fit.recommendation}</span>
                          <p className="text-sm text-stone-700 leading-relaxed">{fit.summary}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                          <p className="overline text-emerald-700 mb-2">Skill matches</p>
                          <ul className="space-y-1">
                            {fit.skill_match?.map((s, i) => <li key={i} className="text-sm text-emerald-800">✓ {s}</li>)}
                          </ul>
                        </div>
                        <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
                          <p className="overline text-red-700 mb-2">Skill gaps</p>
                          <ul className="space-y-1">
                            {fit.skill_gaps?.map((s, i) => <li key={i} className="text-sm text-red-800">✗ {s}</li>)}
                          </ul>
                        </div>
                      </div>

                      {fit.strengths?.length > 0 && (
                        <div className="rounded-2xl bg-white border border-stone-200 p-4">
                          <p className="overline mb-2">Strengths for this role</p>
                          <ul className="space-y-1">
                            {fit.strengths.map((s, i) => <li key={i} className="text-sm text-stone-700">• {s}</li>)}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Resume */}
              {tab === "resume" && (
                <div>
                  {!resume ? (
                    <p className="text-stone-500">No resume submitted yet.</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <ScoreCard label="Overall" score={resume.result?.overall_score} />
                        <ScoreCard label="ATS" score={resume.result?.ats_score} />
                        <ScoreCard label="Keyword match" score={resume.result?.keyword_match} />
                      </div>
                      <div className="rounded-2xl bg-white border border-stone-200 p-5">
                        <p className="overline mb-3">AI Feedback</p>
                        <p className="text-sm text-stone-600 italic mb-3">{resume.result?.summary}</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-emerald-700 mb-1">Strengths</p>
                            <ul className="space-y-1">
                              {resume.result?.strengths?.map((s, i) => <li key={i} className="text-xs text-stone-600">• {s}</li>)}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-red-700 mb-1">Gaps</p>
                            <ul className="space-y-1">
                              {resume.result?.gaps?.map((s, i) => <li key={i} className="text-xs text-stone-600">• {s}</li>)}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Interview */}
              {tab === "interview" && (
                <div>
                  {!interview ? (
                    <p className="text-stone-500">No interview completed yet.</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <ScoreCard label="Overall" score={interview.result?.overall_score} />
                        <ScoreCard label="Clarity" score={interview.result?.clarity} />
                        <ScoreCard label="Depth" score={interview.result?.depth} />
                        <ScoreCard label="Communication" score={interview.result?.communication} />
                      </div>
                      <div className="rounded-2xl bg-white border border-stone-200 p-5">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${
                          interview.result?.verdict?.includes("hire") || interview.result?.verdict?.includes("Hire")
                            ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                            : "bg-red-50 text-red-800 border border-red-200"
                        }`}>{interview.result?.verdict}</span>
                        <p className="text-sm text-stone-700">{interview.result?.summary}</p>
                        {interview.result?.highlights?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-stone-500 mb-2">Highlights</p>
                            <ul className="space-y-1">
                              {interview.result.highlights.map((h, i) => <li key={i} className="text-xs text-stone-600 italic">"{h}"</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-3">
      <p className="text-[10px] uppercase tracking-widest text-stone-400 font-mono">{label}</p>
      <p className="text-sm text-stone-800 mt-0.5 truncate">{value}</p>
    </div>
  );
}

function ScoreCard({ label, score }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-3 text-center">
      <p className="font-display text-2xl" style={{ color: scoreColor(score) }}>{score || "—"}</p>
      <p className="text-[10px] uppercase tracking-widest text-stone-400 font-mono mt-1">{label}</p>
    </div>
  );
}

function scoreColor(s) {
  if (!s) return "#78716C";
  if (s >= 80) return "#15803D";
  if (s >= 60) return "#C2410C";
  return "#78716C";
}

// ----------------------------------------------------------------
// Syndication View (replaces PostsView)
// ----------------------------------------------------------------
const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", color: "#0a66c2", bgColor: "#EFF6FF" },
  { key: "naukri", label: "Naukri", color: "#4a154b", bgColor: "#FAF5FF" },
  { key: "indeed", label: "Indeed", color: "#2557a7", bgColor: "#EFF6FF" },
  { key: "internshala", label: "Internshala", color: "#007bff", bgColor: "#EFF6FF" },
  { key: "shine", label: "Shine", color: "#e6492d", bgColor: "#FFF7ED" },
];

function SyndicationView({ loading, posts, onRefresh, syndicationLinks, jobId }) {
  if (loading)
    return (
      <div className="rounded-3xl bg-white border border-stone-200 p-10 text-stone-500 inline-flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Crafting your platform-native posts…
      </div>
    );
  if (!posts) {
    return (
      <button onClick={onRefresh} className="btn-primary" data-testid="posts-generate">
        Generate posts
      </button>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section 1: Post to job boards */}
      <div className="rounded-3xl bg-white border border-stone-200 p-7">
        <h3 className="font-display text-xl mb-1">Post to job boards</h3>
        <p className="text-stone-500 text-sm mb-5">Open each platform to post this job directly. Copy tracked apply links to monitor where applicants come from.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORMS.map((p) => (
            <PlatformCard
              key={p.key}
              platform={p}
              applyLink={syndicationLinks?.apply_links?.[p.key] || `https://wishire.ai/jobs/${jobId}?src=${p.key}`}
              postLink={syndicationLinks?.post_links?.[p.key] || null}
              jobId={jobId}
            />
          ))}
        </div>
      </div>

      {/* Section 2: AI-written posts */}
      <div>
        <h3 className="font-display text-xl mb-4">AI-written posts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {["linkedin", "naukri", "indeed", "twitter"].map((k) => (
            <PostCard key={k} platform={k} text={posts[k] || ""} />
          ))}
        </div>
      </div>

      {/* Section 3: Indeed auto-feed */}
      <div className="rounded-3xl bg-indigo-50 border border-indigo-200 p-7">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#2557a7] flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">IN</span>
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg text-indigo-900 mb-1">Indeed auto-feed</h3>
            <p className="text-indigo-700 text-sm mb-4">
              Your jobs are automatically included in the Indeed XML feed. Provide this URL to Indeed to syndicate all your open jobs automatically.
            </p>
            <FeedUrlCopy url={syndicationLinks?.indeed_feed_url || "https://wishire.ai/api/feed/indeed.xml"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedUrlCopy({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <code className="px-3 py-2 rounded-xl bg-white border border-indigo-200 text-indigo-800 text-sm font-mono flex-1 truncate min-w-0">{url}</code>
      <button
        onClick={copy}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors text-sm font-medium shrink-0"
      >
        <Copy size={13} /> {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function PlatformCard({ platform, applyLink, postLink, jobId }) {
  const storageKey = `posted_${jobId}_${platform.key}`;
  const [posted, setPosted] = useState(() => {
    try { return !!localStorage.getItem(storageKey); } catch { return false; }
  });
  const [linkCopied, setLinkCopied] = useState(false);

  const handlePostNow = () => {
    if (postLink) {
      window.open(postLink, "_blank", "noopener,noreferrer");
    }
    try { localStorage.setItem(storageKey, "1"); } catch {}
    setPosted(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(applyLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch (e) {}
  };

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span
          className="px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest text-white"
          style={{ background: platform.color }}
        >
          {platform.label}
        </span>
        <span
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
            posted
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-stone-100 border-stone-200 text-stone-500"
          }`}
        >
          {posted ? "Posted" : "Not posted"}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handlePostNow}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-white text-xs font-medium hover:opacity-90 transition-opacity"
          style={{ background: platform.color }}
        >
          Post now →
        </button>
        <button
          onClick={handleCopyLink}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 text-stone-700 text-xs font-medium hover:bg-stone-50 transition-colors"
        >
          <Copy size={11} /> {linkCopied ? "Copied!" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

function PostCard({ platform, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
  };
  const flavor = {
    linkedin: { label: "LinkedIn", color: "#0a66c2" },
    naukri: { label: "Naukri", color: "#4a154b" },
    indeed: { label: "Indeed", color: "#2557a7" },
    twitter: { label: "Twitter / X", color: "#000000" },
  }[platform];
  return (
    <div className="rounded-3xl bg-white border border-stone-200 p-6 flex flex-col" data-testid={`post-${platform}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest text-white" style={{ background: flavor.color }}>
          {flavor.label}
        </span>
        <button onClick={copy} data-testid={`post-${platform}-copy`} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
          <Copy size={14} /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-stone-800 leading-relaxed text-sm flex-1">{text}</pre>
    </div>
  );
}

// ----------------------------------------------------------------
// Distribute — Manatal-style automated distribution view
// ----------------------------------------------------------------
const PLATFORM_META = {
  google_jobs: { name: "Google for Jobs", color: "#4285F4", icon: "🔍" },
  indeed_feed: { name: "Indeed", color: "#2557A7", icon: "📋" },
  linkedin: { name: "LinkedIn", color: "#0A66C2", icon: "💼" },
  webhook: { name: "Webhook / Zapier", color: "#FF4A00", icon: "⚡" },
  naukri: { name: "Naukri", color: "#4A154B", icon: "🇮🇳" },
  internshala: { name: "Internshala", color: "#007BFF", icon: "🎓" },
};

function DistributeView({ job, distributing, distResult, distStatus, onDistribute }) {
  const hasRun = distStatus?.distributed_at;
  const results = distResult?.results || distStatus?.results || {};

  const totalPlatforms = Object.keys(PLATFORM_META).length;
  const activePlatforms = Object.values(results).filter(
    (r) => r?.status === "active" || r?.status === "sent" || r?.status === "share_ready" || r?.status === "deeplink_ready"
  ).length;

  return (
    <div className="space-y-8">
      {/* Hero action card */}
      <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 relative overflow-hidden wsh-grain">
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(600px 300px at 10% 50%, rgba(194,65,12,0.6), transparent 70%)" }}
        />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
              One-click distribution
            </span>
            <h2 className="font-display text-3xl mt-2">
              Reach{" "}
              <span className="text-amber-300">500M+ candidates</span>{" "}
              across 6 platforms.
            </h2>
            <p className="text-stone-300 mt-2 text-sm">
              Google for Jobs, Indeed, LinkedIn, Naukri, Internshala and your webhooks — all in one click.
            </p>
            {hasRun && (
              <p className="mt-2 text-xs text-stone-400">
                Last distributed: {new Date(distStatus.distributed_at).toLocaleString("en-IN")}
                {" · "}{activePlatforms} platforms reached
              </p>
            )}
          </div>
          <button
            onClick={onDistribute}
            disabled={distributing}
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium text-base disabled:opacity-60"
          >
            {distributing ? (
              <><Loader2 size={16} className="animate-spin" /> Distributing…</>
            ) : (
              <><Send size={16} /> {hasRun ? "Re-distribute" : "Distribute now"}</>
            )}
          </button>
        </div>
      </div>

      {/* Platform status grid */}
      {Object.keys(results).length > 0 && (
        <div>
          <h3 className="font-display text-xl mb-4">Distribution status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(PLATFORM_META).map(([key, meta]) => {
              const r = results[key];
              if (!r) return null;
              return (
                <PlatformResult key={key} meta={meta} result={r} platformKey={key} job={job} />
              );
            })}
          </div>
        </div>
      )}

      {/* How it works */}
      {!hasRun && Object.keys(results).length === 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 p-7">
          <h3 className="font-display text-xl mb-5">What happens when you distribute</h3>
          <div className="space-y-4">
            {[
              { icon: "🔍", platform: "Google for Jobs", how: "JSON-LD structured data on your job page gets auto-indexed by Google. Candidates find you in search results." },
              { icon: "📋", platform: "Indeed XML Feed", how: "Your job syncs to Indeed's job index via live XML feed. Indeed re-crawls every 24h." },
              { icon: "💼", platform: "LinkedIn", how: "A pre-filled LinkedIn share URL is generated. Connect your LinkedIn account in Connectors for fully automated posting." },
              { icon: "⚡", platform: "Webhook / Zapier", how: "Job data is POSTed to your webhook URL. Use Zapier/Make to push to any tool — Slack, Notion, Naukri API etc." },
              { icon: "🇮🇳", platform: "Naukri", how: "Deep link to Naukri recruiter portal with pre-filled title and company. One tab opens, you hit post." },
              { icon: "🎓", platform: "Internshala", how: "XML feed submitted to Internshala employer portal. Ideal for fresher roles." },
            ].map((item) => (
              <div key={item.platform} className="flex items-start gap-4">
                <span className="text-2xl shrink-0">{item.icon}</span>
                <div>
                  <p className="font-medium text-stone-800">{item.platform}</p>
                  <p className="text-sm text-stone-500 mt-0.5">{item.how}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-stone-100">
            <p className="text-sm text-stone-500">
              💡 Connect LinkedIn & Zapier once in{" "}
              <Link to="/company/connectors" className="text-[#C2410C] hover:underline font-medium">
                Connectors
              </Link>{" "}
              for fully automated posting on every future job.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformResult({ meta, result, platformKey, job }) {
  const [copied, setCopied] = useState(false);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColor = {
    active: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sent: "bg-emerald-50 border-emerald-200 text-emerald-700",
    share_ready: "bg-blue-50 border-blue-200 text-blue-700",
    deeplink_ready: "bg-amber-50 border-amber-200 text-amber-700",
    skipped: "bg-stone-100 border-stone-200 text-stone-500",
    error: "bg-red-50 border-red-200 text-red-700",
  }[result.status] || "bg-stone-100 border-stone-200 text-stone-500";

  const statusLabel = {
    active: "✓ Active",
    sent: "✓ Sent",
    share_ready: "Share ready",
    deeplink_ready: "Open to post",
    skipped: "Skipped",
    error: "Error",
  }[result.status] || result.status;

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-stone-800">{meta.name}</p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wide ${statusColor}`}>
              {statusLabel}
            </span>
          </div>
          {result.note && <p className="text-xs text-stone-500 mt-0.5">{result.note}</p>}
          {result.error && <p className="text-xs text-red-600 mt-0.5">{result.error}</p>}
          {result.http_status && (
            <p className="text-xs text-stone-400 mt-0.5">HTTP {result.http_status}</p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {result.share_url && (
          <>
            <a
              href={result.share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium transition-colors"
            >
              Share on LinkedIn ↗
            </a>
            <button
              onClick={() => copy(result.share_url)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 text-xs transition-colors"
            >
              <Copy size={11} /> {copied ? "Copied" : "Copy URL"}
            </button>
          </>
        )}
        {result.post_url && (
          <a
            href={result.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 text-xs font-medium transition-colors"
          >
            Open {meta.name} ↗
          </a>
        )}
        {result.feed_url && (
          <button
            onClick={() => copy(result.feed_url)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 text-xs transition-colors"
          >
            <Copy size={11} /> {copied ? "Copied feed URL" : "Copy feed URL"}
          </button>
        )}
      </div>
    </div>
  );
}
